/**
 * @fileoverview Role fetching service
 * 
 * @description
 * Fetches user roles from the database.
 * Used by useUserRole hook.
 * 
 * DATABASE NOTES:
 * - Roles stored in `user_roles` table (NOT on users/profiles!)
 * - RLS uses `has_role()` SECURITY DEFINER function to avoid recursion
 * - One user can have multiple roles
 * 
 * @see useUserRole hook for React integration
 * @see README.md for full documentation
 */

import { supabase } from '@/integrations/supabase/client';
import type { AppRole } from './types';

/**
 * Fetch all roles for a user from the database
 * 
 * @param userId - The user's UUID
 * @returns Array of roles the user has
 * @throws Error if database query fails
 * 
 * @example
 * const roles = await fetchUserRoles('uuid-here');
 * // ['moderator', 'adopter']
 */
export async function fetchUserRoles(userId: string): Promise<AppRole[]> {
  const { data, error } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId);

  if (error) {
    throw new Error(error.message);
  }

  return (data || []).map((r) => r.role as AppRole);
}
